import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import { useEffect, useReducer, useState } from 'react';
import {
    archiveDeclaration,
    deleteDeclaration,
    generateIrelandClearanceDraft,
    generateIrelandSadDraft,
    unarchiveDeclaration,
} from 'store/declarations/client';
import Button from 'components/new-ui/base/button/Button';
import { Col, Row, Space } from 'antd';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import { useNavigate, useParams } from 'react-router-dom';
import useJobs from 'hooks/useJobs';
import useSession from 'hooks/useSession';
import { Declaration } from 'store/declarations/declaration';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import moment from 'moment';
import DeclarationsTable from 'views/custom-declaration/declaration-table/DeclarationsTable';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import Notification from 'components/ui/base/notification/Notification';
import RangePicker from 'components/new-ui/base/date-picker/RangePicker';
import { hasPermissions } from 'config/roles/hasPermissions';
import ArchiveDeclarationModal from 'views/custom-declaration/declaration-table/components/ArchiveDeclarationModal';
import UnarchiveDeclarationModal from 'views/custom-declaration/declaration-table/components/UnarchiveDeclarationModal';
import DeleteDeclarationModal from 'views/custom-declaration/declaration-table/components/DeleteDeclarationModal';
import DocumentsModal from 'views/custom-declaration/declaration-table/components/DocumentsModal';
import BarcodeModal from 'views/custom-declaration/declaration-table/BarcodeModal';
import ManifestJobOverviewHeader from 'views/manifests/job/job-details/ManifestJobOverviewHeader';
import { getErrorMessage } from 'utils/getErrorMessage';
import DeclarationFiltersModal, {
    ModalFilters,
} from 'views/custom-declaration/declaration-table/DeclarationFiltersModal';
import useDeclarationsTable from 'views/custom-declaration/declaration-table/components/useDeclarationsTable';
import DeclarationFilterTags from 'views/custom-declaration/declaration-table/components/DeclarationFilterTags';
import JobOverviewHeader from '../components/JobOverviewHeader';
import { checkIsFreightForwarder } from 'config/roles/roles';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';

interface PrintModalValues {
    visible: boolean;
    declarationId: string | undefined;
}

interface BarcodeModalValues {
    visible: boolean;
    declaration: Declaration | undefined;
}

const OverviewTab = () => {
    const { jobId, manifestId } = useParams();
    const { job, isLoading: isLoadingJob } = useJobs({ jobId, autoFetch: false });
    const { isHaulierUser, isDriverUser, userInfo } = useSession();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const navigate = useNavigate();

    const {
        queryInputVal,
        appliedParams,
        dates,
        tags,
        clearFilter,
        clearAll,
        setDates,
        setPage,
        setQueryInputVal,
        setArchived,
        data: declarations,
        isLoadingDeclarations,
        refetchDeclarations,
        handleTableChange,
        handleFiltersChange,
        selectedIds,
        rowSelection,
        handleExportDeclarationsCSV,
        isExportingCSV,
    } = useDeclarationsTable(jobId);

    const [archiveIds, setArchiveIds] = useState<string[]>([]);
    const [archiveModalVisible, setArchiveModalVisible] = useState<boolean>(false);
    const [unarchiveModalVisible, setUnarchiveModalVisible] = useState<boolean>(false);
    const [filterModalVisible, setFilterModalVisible] = useState<boolean>(false);

    const canCreateDeclarations = hasPermissions(userInfo, 'declarations', 'CREATE');
    const canArchiveDeclarations = hasPermissions(userInfo, 'declarations', 'ARCHIVE');
    const canDeleteDeclarations = hasPermissions(userInfo, 'declarations', 'DELETE');
    const canAssignJobs = hasPermissions(userInfo, 'jobs', 'UPDATE');
    const isUnderManifest = Boolean(manifestId);
    const isFreightForwarderRole = checkIsFreightForwarder(userInfo);

    const {
        size,
        page,
        declarationExternalEntity,
        archived,
        declarationStatus,
        declarationType,
        declarationInternalType,
    } = appliedParams;

    const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
    const [deleteDeclarationId, setDeleteDeclarationId] = useState<string | undefined>(undefined);

    const [printModalState, setPrintModalState] = useReducer(
        (initialState: PrintModalValues, newState: Partial<PrintModalValues>) => ({ ...initialState, ...newState }),
        { visible: false, declarationId: undefined }
    );
    const [barcodeModalState, setBarcodeModalState] = useReducer(
        (initialState: BarcodeModalValues, newState: Partial<BarcodeModalValues>) => ({ ...initialState, ...newState }),
        { visible: false, declaration: undefined }
    );

    const declarationFilters: ModalFilters = {
        query: queryInputVal,
        declarationExternalEntity,
        declarationStatus,
        declarationType,
        declarationInternalType,
    };

    const refreshDeclarations = () => {
        if (!job?.id) return;
        refetchDeclarations({ ...appliedParams, jobId });
    };

    const handleArchive = (ids: string[]) => {
        setArchiveIds(ids);
        setArchiveModalVisible(true);
    };

    const handleUnarchive = (ids: string[]) => {
        setArchiveIds(ids);
        setUnarchiveModalVisible(true);
    };

    const handleOpenBarcodeModal = (declaration: Declaration | undefined) =>
        setBarcodeModalState({ visible: true, declaration });

    const handleDeleteDeclarationButtonClick = (deleteId: string | undefined) => {
        setDeleteDeclarationId(deleteId);
        setDeleteModalVisible(true);
    };

    const handlePrintButtonClick = async (declarationId: string | undefined) => {
        if (!declarationId) return;

        try {
            await Promise.all([generateIrelandSadDraft(declarationId), generateIrelandClearanceDraft(declarationId)]);

            setPrintModalState({ visible: true, declarationId });
        } catch (err) {
            const message = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: message || 'Could not detect selected declaration id. Please try again.',
            });
        }
    };

    const handleDeleteDeclaration = () => {
        if (!deleteDeclarationId) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not detect selected declaration id. Please try again.',
            });
            return;
        }

        deleteDeclaration(deleteDeclarationId)
            .then(() => {
                Notification({
                    type: 'success',
                    placement: 'top',
                    messageTitle: 'Success',
                    description: 'Successfully deleted declaration.',
                });
                refreshDeclarations();
            })
            .catch(() => {
                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Error',
                    description: 'Failed to delete declaration. Please try again.',
                });
            })
            .finally(() => {
                setDeleteModalVisible(false);
                setDeleteDeclarationId(undefined);
            });
    };

    const archiveDeclarations = async () => {
        setArchiveModalVisible(false);
        Promise.all(archiveIds.map((id) => archiveDeclaration(id)))
            .then(() => {
                refreshDeclarations();
                Notification({
                    type: 'success',
                    messageTitle: 'Operation Successful',
                    description: 'Declaration archived successfully!',
                    placement: 'top',
                });
            })
            .catch((err) => {
                const errorMessage = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Operation Failed',
                    description: errorMessage || 'Could not archive the declaration',
                    placement: 'top',
                });
            })
            .finally(() => {
                setArchiveIds([]);
            });
    };

    const unarchiveDeclarations = async () => {
        setUnarchiveModalVisible(false);
        Promise.all(archiveIds.map((id) => unarchiveDeclaration(id)))
            .then((res) => {
                refreshDeclarations();
                Notification({
                    type: 'success',
                    messageTitle: 'Operation Successful',
                    description: 'Declaration unarchived successfully!',
                    placement: 'top',
                });
            })
            .catch((err) => {
                const errorMessage = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Operation Failed',
                    description: errorMessage || 'Could not unarchive the declaration',
                    placement: 'top',
                });
            })
            .finally(() => {
                setArchiveIds([]);
            });
    };

    const handleDuplicate = async (declaration: Declaration) => {
        const country =
            declaration.declarationExternalEntity === DeclarationExternalEntity.REVENUE
                ? DeclarationCountry.IRELAND
                : DeclarationCountry.UK;
        const type = declaration.declarationInternalType;
        navigate(`/invoice-upload/${country}/${type}/job/${declaration.jobId}`, {
            state: { declaration: declaration },
        });
    };

    // Get declarations for the job

    useEffect(() => {
        if (isLoadingJob) {
            showGlobalOverlay({ type: 'LoadingOverlay' });
        } else {
            hideGlobalOverlay();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingJob]);

    return (
        <>
            <StyledPageWrapper>
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    {isUnderManifest ? (
                        <ManifestJobOverviewHeader canAssignJobs={canAssignJobs} />
                    ) : (
                        <JobOverviewHeader
                            job={job}
                            canAssignJobs={canAssignJobs}
                            isFreightForwarderRole={isFreightForwarderRole}
                        />
                    )}

                    {declarations && !isHaulierUser && !isDriverUser && (
                        <Row justify="space-between">
                            <Col>
                                <Space size={8}>
                                    <SearchBar
                                        inputPlaceholder="Search declarations"
                                        value={queryInputVal}
                                        onSearch={(val) => {
                                            setQueryInputVal(val);
                                            setPage(0);
                                        }}
                                        onClear={() => {
                                            setQueryInputVal('');
                                            setPage(0);
                                        }}
                                    />
                                    <Button onClick={() => setFilterModalVisible(true)}>Filter</Button>
                                </Space>
                            </Col>
                            <Col>
                                <Space size={8}>
                                    <ArchiveFilter
                                        value={archived ? 'archived' : 'active'}
                                        onChange={(val) => {
                                            if (val === 'archived') {
                                                setArchived(true);
                                            } else {
                                                setArchived(undefined);
                                            }
                                            setPage(0);
                                        }}
                                    />
                                    <RangePicker
                                        allowClear
                                        value={
                                            dates.startDate && dates.endDate
                                                ? [moment(dates.startDate), moment(dates.endDate)]
                                                : undefined
                                        }
                                        onChange={(values) => {
                                            const [startMoment, endMoment] = values || [];
                                            setDates({
                                                startDate: startMoment ? startMoment.toISOString() : undefined,
                                                endDate: endMoment ? endMoment.toISOString() : undefined,
                                            });
                                            setPage(0);
                                        }}
                                    />
                                    {selectedIds.length > 0 && (
                                        <Button loading={isExportingCSV} onClick={handleExportDeclarationsCSV}>
                                            Export CSV
                                        </Button>
                                    )}
                                </Space>
                            </Col>
                        </Row>
                    )}

                    <DeclarationFilterTags tags={tags} onClearTag={clearFilter} onClearAll={clearAll} />

                    <DeclarationsTable
                        basePath={isUnderManifest ? `/manifests/${manifestId}/${jobId}` : '/declarations'}
                        loading={isLoadingDeclarations}
                        data={declarations}
                        handleDuplicate={canCreateDeclarations ? handleDuplicate : undefined}
                        onArchive={canArchiveDeclarations ? handleArchive : undefined}
                        onUnarchive={canArchiveDeclarations ? handleUnarchive : undefined}
                        onDelete={canDeleteDeclarations ? handleDeleteDeclarationButtonClick : undefined}
                        onPrint={(declarationId: string) => handlePrintButtonClick(declarationId)}
                        onBarcode={handleOpenBarcodeModal}
                        onChange={handleTableChange}
                        pagination={{
                            current: page + 1,
                            pageSize: size,
                            total: declarations?.total,
                            position: ['bottomCenter'],
                            showSizeChanger: true,
                            hideOnSinglePage: true,
                        }}
                        rowSelection={rowSelection}
                    />
                </Space>
            </StyledPageWrapper>

            <ArchiveDeclarationModal
                open={archiveModalVisible}
                onConfirm={archiveDeclarations}
                onCancel={() => setArchiveModalVisible(false)}
            />

            <UnarchiveDeclarationModal
                open={unarchiveModalVisible}
                onConfirm={unarchiveDeclarations}
                onCancel={() => setUnarchiveModalVisible(false)}
            />

            <DeleteDeclarationModal
                open={deleteModalVisible}
                onCancel={() => setDeleteModalVisible(false)}
                onDelete={handleDeleteDeclaration}
            />

            <DocumentsModal
                open={printModalState.visible}
                declarationId={printModalState.declarationId}
                onCancel={() => setPrintModalState({ visible: false })}
            />

            <BarcodeModal
                open={barcodeModalState.visible}
                declaration={barcodeModalState.declaration}
                onCancel={() => setBarcodeModalState({ visible: false })}
            />

            <DeclarationFiltersModal
                open={filterModalVisible}
                onClose={() => setFilterModalVisible(false)}
                onSubmit={() => setFilterModalVisible(false)}
                onFiltersChange={handleFiltersChange}
                filters={declarationFilters}
                onClearAll={() => clearAll()}
                onClearFilter={clearFilter}
            />
        </>
    );
};

export default OverviewTab;
